/**
 * IssueBadge — the single issue-count surface next to the Publish button.
 *
 * Sits in the TopBar just before Publish and answers one question for the
 * reviewer: "is there anything I need to look at before I publish?" The count
 * comes from `summarizeGateIssues(buildGateItem(...))` so it can never disagree
 * with the pre-publish gate dialog — both read the same GateItem.
 *
 * States (categorical — no scores, no model names):
 *  - total === 0           → calm teal "Issues clear" pill (non-interactive)
 *  - blocking > 0          → danger pill "N blocking"
 *  - review > 0 only       → warning pill "N to review"
 *  - both blocking + review → danger pill "X blocking · Y review"
 *
 * When `onClick` is provided the pill becomes a button that jumps the rail to
 * the first/next flagged field (see UniversalEditorPage.handleIssuesClick).
 */
import { AlertTriangle, ShieldCheck, AlertCircle } from 'lucide-react';
import { Pill } from '../../ui';
import type { IssueBadgeSummary } from '@/lib/issueSummary';

export interface IssueBadgeProps {
  summary: IssueBadgeSummary;
  /** When provided, the pill renders as a button and advances the issue cursor. */
  onClick?: () => void;
}

export function IssueBadge({ summary, onClick }: IssueBadgeProps) {
  const { total, blocking, review } = summary;

  if (total === 0) {
    return (
      <Pill tone="teal" size="sm" icon={<ShieldCheck className="size-3" />}>
        Issues clear
      </Pill>
    );
  }

  // Both blocking and review tail → "X blocking · Y review" (danger, since
  // blocking is what stops publish without sign-off).
  if (blocking > 0 && review > 0) {
    return (
      <Pill
        tone="danger"
        size="sm"
        icon={<AlertTriangle className="size-3" />}
        onClick={
          onClick
            ? (e) => {
                e.stopPropagation();
                onClick();
              }
            : undefined
        }
        ariaLabel={`${blocking} blocking, ${review} to review. Jump to first issue.`}
      >
        {blocking} blocking · {review} review
      </Pill>
    );
  }

  if (blocking > 0) {
    return (
      <Pill
        tone="danger"
        size="sm"
        icon={<AlertTriangle className="size-3" />}
        onClick={
          onClick
            ? (e) => {
                e.stopPropagation();
                onClick();
              }
            : undefined
        }
        ariaLabel={`${blocking} blocking. Jump to first issue.`}
      >
        {blocking} blocking
      </Pill>
    );
  }

  // review > 0 only (total > 0 guaranteed by the clear-state early return).
  return (
    <Pill
      tone="warning"
      size="sm"
      icon={<AlertCircle className="size-3" />}
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      ariaLabel={`${review} to review. Jump to first issue.`}
    >
      {review} to review
    </Pill>
  );
}
